import { DOCUMENT_VERSION } from '../config'
import { Page, Row, WebsiteDocument } from '../schema/document'
import { SectionKey, SiteSettings } from '../schema/site'
import { TemplateDefinition, effectiveVariant, templateOf } from './registry'
import { MenuSnapshot } from '../render/menu'
import {
  SectionContext,
  aboutRows,
  extraRows,
  galleryRows,
  heroRows,
  mapRows,
  menuRows,
  reservationRows,
  reviewRows,
  visitRows
} from './sections'
import { everywhere, formatAddress, row } from './shared'
import { normalizeSiteSettings } from './settings'

/**
 * Puts a page together from a template and what the owner decided.
 *
 * Sections appear only when there is something to put in them: no photos means
 * no gallery, no Google place means no review button, and a section switched
 * off is simply not there. The map is the one exception, built empty so the
 * editor can show where it would go; the published page still leaves it out. A template that rendered empty boxes for every field somebody
 * skipped would teach them that the tool is broken.
 *
 * The owner's own blocks follow the section they were placed after, wherever
 * this template happens to put that section. A block placed after a section
 * the template does not have goes last, so nothing typed disappears.
 */
function sectionRows(
  template: TemplateDefinition,
  key: SectionKey,
  ctx: SectionContext
): Row[] {
  const { sections } = ctx.settings
  const style = template.style[key]

  switch (key) {
    case 'hero':
      return heroRows(
        ctx,
        effectiveVariant(template, 'hero', sections.hero.variant),
        style
      )
    case 'menu':
      return sections.menu.enabled
        ? menuRows(
            ctx,
            effectiveVariant(template, 'menu', sections.menu.variant),
            style
          )
        : []
    case 'about':
      return sections.about.enabled
        ? aboutRows(
            ctx,
            effectiveVariant(template, 'about', sections.about.variant),
            style
          )
        : []
    case 'visit':
      return sections.visit.enabled
        ? visitRows(
            ctx,
            effectiveVariant(template, 'visit', sections.visit.variant),
            style
          )
        : []
    case 'gallery':
      return sections.gallery.enabled
        ? galleryRows(
            ctx,
            effectiveVariant(template, 'gallery', sections.gallery.variant),
            style
          )
        : []
    case 'reservations':
      return sections.reservations.enabled ? reservationRows(ctx) : []
    case 'map':
      return sections.map.enabled ? mapRows(ctx, sections.map.height) : []
    case 'review':
      return sections.review.enabled
        ? reviewRows(ctx, sections.review.label)
        : []
    default:
      return []
  }
}

/** A thin line before a section, for a template that separates with lines. */
const separatorRow = (key: SectionKey): Row =>
  row(
    `tpl-row-divider-${key}`,
    '1',
    [
      {
        id: `tpl-col-divider-${key}`,
        align: 'left',
        items: [{ id: `tpl-divider-${key}`, type: 'divider' }]
      }
    ],
    { paddingY: 'S' }
  )

/** What the page is drawn against besides the settings. */
export interface ComposeOptions {
  /** The venue's menu as it stands; without it there is no menu section. */
  menu?: MenuSnapshot | null
}

export function composeDocument(
  template: TemplateDefinition,
  settings: SiteSettings,
  languages: string[],
  defaultLanguage: string,
  theme: WebsiteDocument['theme'],
  options: ComposeOptions = {}
): WebsiteDocument {
  const ctx: SectionContext = {
    settings,
    languages,
    defaultLanguage,
    address: formatAddress(settings),
    menu: options.menu
  }
  const blocks: Row[] = []

  template.order.forEach(key => {
    const rows = sectionRows(template, key, ctx)
    if (rows.length > 0 && template.separators && blocks.length > 0) {
      blocks.push(separatorRow(key))
    }
    blocks.push(...rows)
    settings.sections.extras
      .filter(extra => extra.after === key)
      .forEach(extra => blocks.push(...extraRows(ctx, extra)))
  })

  settings.sections.extras
    .filter(extra => !template.order.includes(extra.after))
    .forEach(extra => blocks.push(...extraRows(ctx, extra)))

  const heroPhoto = settings.sections.hero.photo

  const page: Page = {
    id: 'tpl-page-home',
    slug: '',
    title: everywhere(languages, settings.name),
    seo: {
      title: everywhere(
        languages,
        [settings.name, settings.city].filter(Boolean).join(' - ')
      ),
      description: Object.fromEntries(
        languages.map(language => [
          language,
          (
            settings.tagline[language] ??
            settings.tagline[defaultLanguage] ??
            ''
          ).slice(0, 155)
        ])
      ),
      ...(heroPhoto ? { ogImageAssetId: heroPhoto } : {})
    },
    status: 'published',
    blocks
  }

  return {
    version: DOCUMENT_VERSION,
    templateId: template.id,
    venue: settings,
    languages,
    defaultLanguage,
    theme,
    pages: [page]
  }
}

/**
 * The page for what the owner typed, in the template they chose.
 *
 * Whatever shape the settings arrive in: a document written before sections
 * existed is lifted first, so the form and the preview never see one without
 * them.
 */
export function buildDocument(
  settings: SiteSettings,
  languages: string[],
  defaultLanguage: string,
  theme: WebsiteDocument['theme'],
  options: ComposeOptions = {}
): WebsiteDocument {
  const normalized = normalizeSiteSettings(settings, defaultLanguage)
  return composeDocument(
    templateOf(normalized),
    normalized,
    languages,
    defaultLanguage,
    theme,
    options
  )
}

